import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useInvestments } from "../hooks/useInvestments";
import { useSearchParams, useNavigate } from "react-router-dom";
import { formatAmount } from "../config/currencies";
import {
  TrendingUp, Wallet, Plus, Minus, CircleCheck, AlertTriangle, Search, X,
  Undo2, Pencil, Trash2, RefreshCw, BarChart3, PieChart, Package, ArrowUp, ArrowDown, ChevronUp, ChevronDown,
} from "lucide-react";

export default function Inversiones() {
  const { profile, lang, currency } = useAuth();
  const navigate = useNavigate();
  const {
    investments, loading, addInvestment, withdrawInvestment, updateInvestment, deleteInvestment,
    getMonthlyHistory, currentMonth, currentYear,
    availableToInvest, pct, getMonthPoolRemaining, getYearPoolRemaining,
    totalInvested,
  } = useInvestments();
  const c = profile?.config || {};
  const fmt = n => formatAmount(n, currency);

  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const MONTHS_EN = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];

  const [mode,   setMode]   = useState("add"); // "add" (Aportar) | "withdraw" (Retirar)
  const [amount, setAmount] = useState("");
  const [note,   setNote]   = useState("");
  const [platform, setPlatform] = useState("");
  const [assetAmount, setAssetAmount] = useState(""); // legado: texto libre, ej. "0,008 ETH"
  const [symbol, setSymbol] = useState("");     // ticker real, ej. SPCX, SOL, NVDA
  const [quantity, setQuantity] = useState(""); // cantidad numérica limpia
  const [entryDate, setEntryDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  });

  // Buscador de criptomonedas (CoinGecko, API pública gratis, sin clave —
  // se llama directamente desde el navegador del usuario, sin pasar por
  // ningún servidor propio, así que no tiene coste para nadie).
  const [cryptoQuery, setCryptoQuery] = useState("");
  const [cryptoResults, setCryptoResults] = useState([]);
  const [cryptoSearching, setCryptoSearching] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null); // {id, symbol, name}
  const [coinPriceEur, setCoinPriceEur] = useState(null);
  const [cryptoError, setCryptoError] = useState("");
  const cryptoDebounceRef = useRef(null);

  function handleCryptoQueryChange(v) {
    setCryptoQuery(v);
    setCryptoError("");
    if (cryptoDebounceRef.current) clearTimeout(cryptoDebounceRef.current);
    if (!v.trim() || v.trim().length < 2) { setCryptoResults([]); return; }
    cryptoDebounceRef.current = setTimeout(async () => {
      setCryptoSearching(true);
      try {
        const res = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(v.trim())}`);
        if (!res.ok) throw new Error("rate-limit");
        const data = await res.json();
        setCryptoResults((data.coins || []).slice(0, 8));
      } catch (err) {
        setCryptoError(lang === "en"
          ? "Couldn't search right now — try again in a moment."
          : "No se pudo buscar ahora mismo — prueba en un momento.");
        setCryptoResults([]);
      } finally {
        setCryptoSearching(false);
      }
    }, 450);
  }

  // Consulta el precio de una cripto: si la fecha elegida es hoy, el
  // precio actual; si es una fecha pasada, el precio histórico de ESE día
  // exacto (CoinGecko guarda histórico diario desde el lanzamiento de cada
  // moneda, así que sirve para inversiones antiguas).
  async function fetchPriceForDate(coinId, dateStr) {
    const today = new Date().toISOString().slice(0, 10);
    if (!dateStr || dateStr === today) {
      const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${coinId}&vs_currencies=eur`);
      if (!res.ok) throw new Error("rate-limit");
      const data = await res.json();
      return data?.[coinId]?.eur ?? null;
    }
    // CoinGecko quiere la fecha histórica en formato DD-MM-AAAA
    const [y, m, d] = dateStr.split("-");
    const res = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}/history?date=${d}-${m}-${y}`);
    if (!res.ok) throw new Error("rate-limit");
    const data = await res.json();
    return data?.market_data?.current_price?.eur ?? null;
  }

  async function loadCoinPrice(coin, dateStr) {
    setCoinPriceEur(null);
    setCryptoError("");
    try {
      const price = await fetchPriceForDate(coin.id, dateStr);
      if (price) setCoinPriceEur(price);
      else setCryptoError(lang === "en"
        ? "No price found for that date — this coin may not have existed yet."
        : "No se encontró precio para esa fecha — puede que esta cripto aún no existiera.");
    } catch (err) {
      setCryptoError(lang === "en"
        ? "Couldn't fetch the price right now — you can still enter the quantity by hand."
        : "No se pudo consultar el precio ahora mismo — puedes poner la cantidad a mano igualmente.");
    }
  }

  async function selectCoin(coin) {
    setSelectedCoin(coin);
    setSymbol(coin.symbol.toUpperCase());
    setCryptoQuery("");
    setCryptoResults([]);
    await loadCoinPrice(coin, entryDate);
  }

  // Si cambias la fecha DESPUÉS de haber elegido una cripto, se vuelve a
  // consultar el precio para la nueva fecha automáticamente.
  useEffect(() => {
    if (selectedCoin) loadCoinPrice(selectedCoin, entryDate);
  }, [entryDate]);

  // Mientras haya una cripto elegida con precio (de hoy o histórico), la
  // cantidad se recalcula sola cada vez que cambias el importe en euros.
  useEffect(() => {
    if (selectedCoin && coinPriceEur && amount) {
      const amt = parseFloat(String(amount).replace(",", "."));
      if (amt > 0) setQuantity(String(amt / coinPriceEur));
    }
  }, [amount, coinPriceEur, selectedCoin]);

  function clearSelectedCoin() {
    setSelectedCoin(null);
    setCoinPriceEur(null);
    setCryptoError("");
  }

  // Precios en vivo para "Totales por activo" (ganancia/pérdida). Se piden
  // solo cuando el usuario pulsa "Actualizar precios" — nunca automático,
  // para no gastar peticiones del límite compartido sin que lo pidas.
  const [livePrices, setLivePrices] = useState({}); // { coingeckoId: precioEur }
  const [resolvedBySymbol, setResolvedBySymbol] = useState({}); // { "SOL": "solana" } — resuelto automáticamente para aportaciones antiguas
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesError, setPricesError] = useState("");
  const [pricesUpdatedAt, setPricesUpdatedAt] = useState(null);

  const [error,  setError]  = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [assetSearch, setAssetSearch] = useState("");
  // Rango del gráfico "Comparativa mes a mes" — en meses (3, 12, 60) o
  // Infinity para "todo". Independiente del buscador de arriba: controla
  // solo cuántos meses se dibujan en el gráfico, para que no se sature con
  // el tiempo.
  const [chartRange, setChartRange] = useState(12);

  // Si se llega desde Gráficos al pulsar un activo concreto en el donut de
  // "Inversiones por activo" (?asset=cripto), se muestra directamente el
  // histórico de ESE activo con todas sus fechas, sin importar el filtro
  // de mes/año. Se usa un efecto (no solo el estado inicial) porque React
  // Router no vuelve a montar la página si ya estabas en /inversiones —
  // igual que se corrigió antes en Historial.
  const [searchParams] = useSearchParams();
  const [assetFocus, setAssetFocus] = useState(searchParams.get("asset") || null);
  // Cuántas compras de un activo mostrar antes de necesitar "Ver más" — se
  // reinicia cada vez que se cambia de activo, para no arrastrar el
  // "ver más" de uno al abrir otro.
  const PURCHASES_PAGE_SIZE = 5;
  const [visiblePurchases, setVisiblePurchases] = useState(PURCHASES_PAGE_SIZE);

  // Al entrar al detalle de un activo (clic en una fila, o llegando desde
  // Gráficos), se consulta el precio en vivo automáticamente — sigue
  // siendo una sola acción puntual (la de entrar), no una consulta
  // continua de fondo.
  useEffect(() => {
    if (assetFocus) refreshPrices();
  }, [assetFocus]);
  useEffect(() => {
    setAssetFocus(searchParams.get("asset") || null);
  }, [searchParams]);

  // Al entrar en la página de Inversiones (una sola vez, no cada vez que
  // algo se recalcula), se consultan los precios automáticamente — para
  // no tener que pulsar "Actualizar" a mano cada vez.
  const autoFetchedRef = useRef(false);

  // Desplaza automáticamente hasta la fila del activo que se despliega —
  // si no, al llegar desde Gráficos (o al pulsar una fila) la página se
  // queda arriba del todo y parece que "no ha hecho nada", aunque el
  // acordeón sí se haya abierto más abajo.
  const expandedRowRef = useRef(null);
  useEffect(() => {
    if (assetFocus && expandedRowRef.current) {
      expandedRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [assetFocus]);
  useEffect(() => {
    if (!loading && !autoFetchedRef.current) {
      autoFetchedRef.current = true;
      refreshPrices();
    }
  }, [loading]);

  // Filtro de búsqueda: por año y/o mes
  // Por defecto se muestra "Todos" (el total de siempre) — el propio
  // buscador permite luego elegir un mes o un año concretos.
  const [fYear,  setFYear]  = useState("Todos");
  const [fMonth, setFMonth] = useState("Todos");

  // Si se llega desde el gráfico de barras "Inversión por mes" en Gráficos
  // (?year=2026&month=Julio), se selecciona directamente ese mes en el
  // buscador de abajo — mismo motivo del efecto (no solo estado inicial)
  // que el de assetFocus más arriba: React Router no remonta la página.
  // Si en cambio se llega solo con "?asset=" (desde el donut de activos, o
  // al pulsar una fila aquí mismo), se vuelve a "Todos" — si no, un mes
  // que hubiera quedado seleccionado de antes podría no incluir ninguna
  // aportación de ese activo, y la fila nunca se encontraría para
  // desplegar el acordeón.
  useEffect(() => {
    const y = searchParams.get("year");
    const m = searchParams.get("month");
    const a = searchParams.get("asset");
    if (y || m) {
      setFYear(y || "Todos");
      setFMonth(m || "Todos");
    } else if (a) {
      setFYear("Todos");
      setFMonth("Todos");
    }
  }, [searchParams]);

  // Edición de aportaciones/retiros ya guardados
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editPlatform, setEditPlatform] = useState("");
  const [editAssetAmount, setEditAssetAmount] = useState("");
  const [editSymbol, setEditSymbol] = useState("");
  const [editQuantity, setEditQuantity] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Buscador de cripto dentro de la edición (mismo mecanismo que al
  // añadir, pero con su propio estado independiente).
  const [editCryptoQuery, setEditCryptoQuery] = useState("");
  const [editCryptoResults, setEditCryptoResults] = useState([]);
  const [editCryptoSearching, setEditCryptoSearching] = useState(false);
  const [editCoingeckoId, setEditCoingeckoId] = useState(null);
  const [editAssetName, setEditAssetName] = useState(null);
  const editCryptoDebounceRef = useRef(null);

  function handleEditCryptoQueryChange(v) {
    setEditCryptoQuery(v);
    if (editCryptoDebounceRef.current) clearTimeout(editCryptoDebounceRef.current);
    if (!v.trim() || v.trim().length < 2) { setEditCryptoResults([]); return; }
    editCryptoDebounceRef.current = setTimeout(async () => {
      setEditCryptoSearching(true);
      try {
        const res = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(v.trim())}`);
        if (!res.ok) throw new Error("rate-limit");
        const data = await res.json();
        setEditCryptoResults((data.coins || []).slice(0, 8));
      } catch (err) {
        setEditCryptoResults([]);
      } finally {
        setEditCryptoSearching(false);
      }
    }, 450);
  }

  function selectEditCoin(coin) {
    setEditSymbol(coin.symbol.toUpperCase());
    setEditCoingeckoId(coin.id);
    setEditAssetName(coin.name);
    setEditCryptoQuery("");
    setEditCryptoResults([]);
  }

  function startEditInv(i) {
    setEditingId(i.id);
    setEditAmount(String(Math.abs(i.amount)));
    setEditNote(i.note || "");
    setEditPlatform(i.platform || "");
    setEditAssetAmount(i.assetAmount || "");
    setEditSymbol(i.symbol || "");
    setEditQuantity(i.quantity !== null && i.quantity !== undefined ? String(Math.abs(i.quantity)) : "");
    // Se parte del id/nombre que ya tuviera guardado — si el usuario busca
    // y elige una cripto distinta, se sustituyen por los nuevos.
    setEditCoingeckoId(i.coingeckoId || null);
    setEditAssetName(i.assetName || null);
    setEditCryptoQuery("");
    setEditCryptoResults([]);
  }

  async function saveEditInv(i) {
    const amt = parseFloat(String(editAmount).replace(",", "."));
    if (!amt || amt <= 0) {
      setError(lang === "en" ? "Enter a valid amount" : "Introduce un importe válido");
      return;
    }
    setEditSaving(true);
    try {
      const symbolUnchanged = (editSymbol || "").trim().toUpperCase() === (i.symbol || "");
      await updateInvestment(i, {
        amount: amt, note: editNote, platform: editPlatform,
        assetAmount: editAssetAmount, symbol: editSymbol, quantity: editQuantity,
        // Si se buscó y eligió una cripto nueva, se usa ese id/nombre. Si
        // no, y el símbolo no cambió respecto al original, se conserva lo
        // que ya tenía. Si se cambió el símbolo a mano sin buscar, se
        // descarta — ya no sabemos con certeza a qué cripto corresponde.
        coingeckoId: editCoingeckoId || (symbolUnchanged ? i.coingeckoId : null),
        assetName: editAssetName || (symbolUnchanged ? i.assetName : null),
      });
      setEditingId(null);
    } catch (err) {
      console.error("Error al editar la aportación:", err);
      setError(lang === "en" ? `Something went wrong: ${err.message || err}` : `Algo falló: ${err.message || err}`);
    } finally {
      setEditSaving(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    setError("");
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!amt || amt <= 0) {
      setError(lang === "en" ? "Enter a valid amount" : "Introduce un importe válido");
      return;
    }
    setSaving(true);
    try {
      const coingeckoId = selectedCoin?.id || null;
      const assetName = selectedCoin?.name || null;
      if (mode === "withdraw") {
        await withdrawInvestment({ amount: amt, note, platform, assetAmount, symbol, assetName, quantity, coingeckoId, date: entryDate });
        setSuccess(lang === "en" ? "Withdrawal made" : "Retiro realizado");
      } else {
        await addInvestment({ amount: amt, note, platform, assetAmount, symbol, assetName, quantity, coingeckoId, date: entryDate });
        setSuccess(lang === "en" ? "Contribution added" : "Aportación realizada");
      }
      setAmount(""); setNote(""); setPlatform(""); setAssetAmount(""); setSymbol(""); setQuantity("");
      setSelectedCoin(null); setCoinPriceEur(null); setCryptoQuery(""); setCryptoResults([]); setCryptoError("");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error en Inversiones:", err);
      setError(lang === "en"
        ? `Something went wrong: ${err.message || err}`
        : `Algo falló: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  }

  // Caché de símbolo -> id de CoinGecko ya resuelto, para no tener que
  // volver a buscarlo cada vez que se pulsa "Actualizar precios" en la
  // misma sesión. IMPORTANTE: tiene que declararse aquí, antes del
  // "if (loading) return" de abajo — los hooks de React siempre tienen que
  // ejecutarse en el mismo orden en cada render, nunca después de un
  // return condicional (si no, React lanza el error #310).
  const resolvedIdsRef = useRef({});

  if (loading) return <div className="page"><p style={{color:"var(--gray-400)"}}>Cargando...</p></div>;

  const history = getMonthlyHistory(); // más reciente primero, con diff/diffPct

  // Años disponibles para el desplegable, a partir de las aportaciones reales
  const years = [...new Set(investments.map(i => i.year))].sort((a, b) => b - a);

  const filteredHistory = history.filter(m =>
    (fYear === "Todos"  || m.year === parseInt(fYear)) &&
    (fMonth === "Todos" || m.month === fMonth)
  );

  // El gráfico respeta el mismo filtro; sin filtro, se limita al rango
  // elegido con los botones de periodo (3M/1A/5A), para que no se sature
  // con el tiempo.
  const chartSource = (fYear === "Todos" && fMonth === "Todos")
    ? filteredHistory.slice(0, chartRange === Infinity ? undefined : chartRange)
    : filteredHistory;
  const chartData = [...chartSource].reverse(); // orden cronológico

  // "Disponible para invertir": respeta el buscador igual que "Total
  // invertido" — por defecto (mes en curso ya seleccionado) muestra ese
  // mes; si se cambia a un año concreto, muestra el restante de todo ese
  // año; y si se pone en "Todos los años"/"Todos los meses", muestra el
  // disponible acumulado de siempre.
  const hasSpecificMonthFilter = fYear !== "Todos" && fMonth !== "Todos";
  const isAllTime = fYear === "Todos" && fMonth === "Todos";
  const availableCardValue = isAllTime
    ? availableToInvest
    : hasSpecificMonthFilter
      ? getMonthPoolRemaining(fMonth, parseInt(fYear))
      : fYear !== "Todos"
        ? getYearPoolRemaining(parseInt(fYear))
        : getMonthPoolRemaining(fMonth, currentYear); // solo mes, sin año -> se asume el año en curso
  const availableCardLabel = isAllTime
    ? (lang === "en" ? "Available to invest (all time)" : "Disponible para invertir (de siempre)")
    : hasSpecificMonthFilter
      ? `${fMonth} ${fYear}`
      : fYear !== "Todos"
        ? `${lang === "en" ? "Year" : "Año"} ${fYear}`
        : `${fMonth} ${currentYear}`;
  const availableCardSub = isAllTime
    ? (lang === "en"
        ? "Everything auto-set aside, minus what's already invested"
        : "Todo lo apartado automáticamente, menos lo ya invertido")
    : (lang === "en"
        ? `Remaining from that period's fund (${pct}%)`
        : `Restante del fondo de ese periodo (${pct}%)`);

  // Movimientos del mes que se esté viendo (para poder borrar aportaciones
  // concretas) — respeta el filtro si hay un mes+año concretos elegidos, o
  // el mes real en curso si no. Se excluyen los "migrated": no son
  // aportaciones reales que hiciera el usuario.
  const listMonth = hasSpecificMonthFilter ? fMonth : currentMonth;
  const listYear  = hasSpecificMonthFilter ? parseInt(fYear) : currentYear;
  const currentMonthEntries = investments.filter(
    i => !i.migrated && i.month === listMonth && i.year === listYear
  );

  // Ancho de "lienzo" FIJO, independiente del número de meses — así el
  // gráfico mantiene siempre la misma proporción y nunca se ve estirado o
  // desproporcionado cuando hay pocos meses (2-3) dentro de una tarjeta ancha.
  const chartViewW = 480;
  const nBars = Math.max(chartData.length, 1);
  const slot = chartViewW / nBars;
  const chartH = 110;

  // Donut: qué mes se invirtió más, sobre el mismo conjunto de meses que
  // ya muestra el gráfico de barras (respeta el mismo filtro).
  const donutColors = ["#9C7A16","#1B7F3A","#4f46e5","#C0392B","#7C6FF0","#0FB8A6","#D89B1F","#34D399"];
  const donutTotal = chartData.reduce((s, m) => s + m.total, 0);
  const donutCircumference = 2 * Math.PI * 50;
  let donutAcc = 0;
  const donutSegments = chartData.map((m, i) => {
    const segPct = donutTotal > 0 ? (m.total / donutTotal) * 100 : 0;
    const len = donutCircumference * (segPct / 100);
    const seg = { ...m, color: donutColors[i % donutColors.length], segPct, len, offset: donutAcc };
    donutAcc += len;
    return seg;
  });
  const topMonth = chartData.length > 0
    ? chartData.reduce((max, m) => (m.total > max.total ? m : max), chartData[0])
    : null;

  // Agrupación por activo (usando la "Nota" como nombre, ej. "cripto",
  // "Nvidia"): suma el total neto en € (aportado menos retirado) y, si se
  // puede interpretar la cantidad escrita en "Cantidad del activo" (ej.
  // "0,008 ETH"), también suma la cantidad total del activo. Se calcula
  // sobre TODO el histórico (no depende del buscador de arriba), porque lo
  // que tienes acumulado de un activo no se "reinicia" cada mes.
  function parseAssetQty(str) {
    if (!str) return null;
    const m = String(str).trim().match(/^(-?[\d.,]+)\s*(.*)$/);
    if (!m) return null;
    const num = parseFloat(m[1].replace(",", "."));
    if (isNaN(num)) return null;
    return { num, unit: (m[2] || "").trim() };
  }

  // Texto a mostrar para la cantidad del activo: prioriza el modelo nuevo
  // (symbol + quantity, números limpios), y si no existe, cae al texto
  // libre antiguo (assetAmount) por compatibilidad con aportaciones viejas.
  function assetLabel(i) {
    if (i.quantity !== null && i.quantity !== undefined && i.symbol) {
      return `${Math.abs(i.quantity)} ${i.symbol}`;
    }
    return i.assetAmount || "";
  }

  const UNSPECIFIED_LABEL = lang === "en" ? "Unspecified" : "Sin especificar";
  const assetGroups = (() => {
    const groups = {};
    investments
      .filter(i => !i.migrated && (i.symbol || i.note || i.platform))
      // Respeta el mismo buscador de mes/año de arriba de la página — con
      // "Todos"/"Todos" se ve el total de siempre; con un mes concreto
      // elegido, solo las aportaciones de ese mes.
      .filter(i => (fYear === "Todos" || i.year === parseInt(fYear)) && (fMonth === "Todos" || i.month === fMonth))
      .forEach(i => {
        // El símbolo real (si existe) es la clave de agrupación más fiable
        // — antes solo se agrupaba por la "Nota" de texto libre, lo que
        // rompía si escribías el mismo activo con nombres distintos.
        // OJO: nunca se usa "platform" (Revolut, Binance...) como nombre
        // del activo — es DÓNDE se invirtió, no QUÉ se compró. Las
        // aportaciones que solo tienen plataforma (sin símbolo ni nota) se
        // agrupan aparte, en "Sin especificar", para que se puedan
        // localizar y arreglar en vez de desaparecer sin más de la vista.
        const displayName = i.symbol || i.note ? (i.symbol || i.note).trim() : UNSPECIFIED_LABEL;
        const key = displayName.trim().toLowerCase();
        if (!groups[key]) {
          groups[key] = { name: displayName.trim(), total: 0, qty: 0, unit: i.symbol || null, qtyBroken: false, count: 0, symbol: i.symbol || null, assetName: i.assetName || null };
        }
        if (i.assetName && !groups[key].assetName) groups[key].assetName = i.assetName;
        const g = groups[key];
        g.total += i.amount || 0;
        g.count += 1;
        if (i.coingeckoId && !g.coingeckoId) g.coingeckoId = i.coingeckoId;
        // Se prioriza la cantidad numérica estructurada (quantity+symbol);
        // solo se recurre al texto libre antiguo si esta aportación no
        // tiene esos campos nuevos.
        if (i.quantity !== null && i.quantity !== undefined && i.symbol) {
          if (g.unit === null) g.unit = i.symbol;
          if (g.unit.toLowerCase() === i.symbol.toLowerCase()) {
            g.qty += i.quantity;
          } else {
            g.qtyBroken = true;
          }
        } else {
          const parsed = parseAssetQty(i.assetAmount);
          if (parsed) {
            const signedQty = i.isWithdrawal ? -parsed.num : parsed.num;
            if (g.unit === null) g.unit = parsed.unit;
            if (g.unit.toLowerCase() === parsed.unit.toLowerCase()) {
              g.qty += signedQty;
            } else {
              g.qtyBroken = true; // unidades distintas dentro del mismo grupo, no se puede sumar con garantías
            }
          }
        }
      });
    return Object.values(groups).sort((a, b) => b.total - a.total);
  })();

  // Buscador por nombre/símbolo dentro de "Totales por activo" — útil
  // cuando ya hay muchos activos distintos. Se calcula también un total
  // invertido + beneficio/pérdida agregado, solo de los resultados
  // encontrados por la búsqueda.
  const displayedAssetGroups = assetSearch.trim()
    ? assetGroups.filter(g => {
        const q = assetSearch.trim().toLowerCase();
        return g.name.toLowerCase().includes(q) || (g.assetName && g.assetName.toLowerCase().includes(q));
      })
    : assetGroups;
  const searchInvested = displayedAssetGroups.reduce((s, g) => s + g.total, 0);
  const searchCurrent = displayedAssetGroups.reduce((sum, g) => {
    const effectiveId = g.coingeckoId || (g.symbol ? resolvedBySymbol[g.symbol] : null);
    const livePrice = effectiveId ? livePrices[effectiveId] : null;
    const canShow = livePrice && !g.qtyBroken && g.qty > 0;
    return sum + (canShow ? g.qty * livePrice : g.total);
  }, 0);
  const searchDiff = searchCurrent - searchInvested;
  const searchDiffPct = searchInvested > 0 ? (searchDiff / searchInvested) * 100 : null;
  const searchHasAnyLive = displayedAssetGroups.some(g => {
    const effectiveId = g.coingeckoId || (g.symbol ? resolvedBySymbol[g.symbol] : null);
    return effectiveId && livePrices[effectiveId];
  });

  // Total invertido + beneficio/pérdida: por defecto es el total de
  // siempre, pero si cambias el mes/año en el buscador de abajo, se
  // recalcula solo para ese periodo — invertido y beneficio de ESE mes.
  // Para los activos con precio en vivo (cripto reconocida) se usa su
  // valor actual; para el resto (SpaceX, S&P500 escritos a mano...), se
  // cuenta tal cual su importe, ya que no hay forma gratuita de saber su
  // valor de mercado real — así nunca se "inventa" una ganancia que no se
  // puede confirmar. Los retiros se cuentan igual en ambos lados (no
  // aportan ganancia/pérdida, solo mueven dinero de vuelta).
  const periodInvestments = investments.filter(i => {
    if (i.migrated) return false;
    if (fYear !== "Todos" && i.year !== parseInt(fYear)) return false;
    if (fMonth !== "Todos" && i.month !== fMonth) return false;
    return true;
  });
  const periodInvested = periodInvestments.reduce((s, i) => s + (i.amount || 0), 0);
  const periodCurrent = periodInvestments.reduce((sum, i) => {
    if (i.isWithdrawal) return sum + (i.amount || 0);
    const effectiveId = i.coingeckoId || (i.symbol ? resolvedBySymbol[i.symbol] : null);
    const livePrice = effectiveId ? livePrices[effectiveId] : null;
    const qty = (i.quantity !== null && i.quantity !== undefined) ? Math.abs(i.quantity) : null;
    if (livePrice && qty) return sum + qty * livePrice;
    return sum + (i.amount || 0);
  }, 0);
  const periodDiff = periodCurrent - periodInvested;
  const periodDiffPct = periodInvested > 0 ? (periodDiff / periodInvested) * 100 : null;
  const periodHasAnyLive = periodInvestments.some(i => {
    if (i.isWithdrawal) return false;
    const effectiveId = i.coingeckoId || (i.symbol ? resolvedBySymbol[i.symbol] : null);
    return effectiveId && livePrices[effectiveId];
  });
  const periodTotalLabel = (() => {
    if (fYear === "Todos" && fMonth === "Todos") return lang === "en" ? "Total invested (all time)" : "Total invertido (de siempre)";
    if (fYear !== "Todos" && fMonth !== "Todos") return `${lang === "en" ? "Invested in" : "Invertido en"} ${fMonth} ${fYear}`;
    if (fYear !== "Todos") return `${lang === "en" ? "Year" : "Año"} ${fYear}`;
    return `${fMonth} (${lang === "en" ? "all years" : "todos los años"})`;
  })();

  // Para las aportaciones antiguas que tienen "Símbolo" (ej. "SOL") pero
  // no tienen guardado el id interno de CoinGecko (porque se crearon antes
  // de que empezáramos a guardarlo), se intenta resolver automáticamente
  // buscando ese símbolo — si hay una coincidencia exacta y clara, se usa
  // sin que el usuario tenga que volver a introducir la aportación. Si no
  // se encuentra nada (ej. es una acción como "SPCX", no una cripto), se
  // ignora sin dar error.
  async function resolveSymbolToId(symbol) {
    if (resolvedIdsRef.current[symbol]) return resolvedIdsRef.current[symbol];
    try {
      const res = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(symbol)}`);
      if (!res.ok) return null;
      const data = await res.json();
      const match = (data.coins || []).find(c => c.symbol?.toUpperCase() === symbol.toUpperCase());
      if (match) {
        resolvedIdsRef.current[symbol] = match.id;
        return match.id;
      }
    } catch (err) { /* se ignora — simplemente no se podrá mostrar precio para este activo */ }
    return null;
  }

  // Consulta de golpe el precio actual de todas las criptos — una sola
  // llamada para todas, no una por cada una — se llama solo al pulsar el
  // botón, nunca automático.
  async function refreshPrices() {
    setPricesLoading(true);
    setPricesError("");
    try {
      // 1) Ids ya guardados directamente (aportaciones nuevas)
      const directIds = assetGroups.filter(g => g.coingeckoId).map(g => g.coingeckoId);
      // 2) Para las que solo tienen símbolo (aportaciones antiguas), se
      //    intenta resolver el id buscando ese símbolo.
      const needResolve = assetGroups.filter(g => !g.coingeckoId && g.symbol);
      const resolved = await Promise.all(needResolve.map(g => resolveSymbolToId(g.symbol)));
      // Se guarda el mapa símbolo->id resuelto para poder aplicarlo también
      // al pintar (además del caché en la ref, para forzar el re-render).
      const bySymbol = {};
      needResolve.forEach((g, i) => { if (resolved[i]) bySymbol[g.symbol] = resolved[i]; });
      setResolvedBySymbol(prev => ({ ...prev, ...bySymbol }));

      const ids = [...new Set([...directIds, ...resolved.filter(Boolean)])];
      if (ids.length === 0) {
        setPricesError(lang === "en"
          ? "None of your assets could be matched to a known crypto."
          : "Ninguno de tus activos se pudo relacionar con una cripto conocida.");
        return;
      }
      const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${ids.join(",")}&vs_currencies=eur`);
      if (!res.ok) throw new Error("rate-limit");
      const data = await res.json();
      const prices = {};
      ids.forEach(id => { if (data?.[id]?.eur) prices[id] = data[id].eur; });
      setLivePrices(prices);
      setPricesUpdatedAt(new Date());
    } catch (err) {
      setPricesError(lang === "en"
        ? "Couldn't update prices right now — try again in a moment."
        : "No se pudieron actualizar los precios ahora mismo — prueba en un momento.");
    } finally {
      setPricesLoading(false);
    }
  }

  // Si venimos de Gráficos con un activo concreto (?asset=cripto), se
  // recopila TODO su histórico (todas las fechas, sin importar mes/año)
  // para mostrarlo en una sección destacada arriba de todo.
  const assetFocusItems = assetFocus
    ? investments
        .filter(i => {
          if (i.migrated) return false;
          const displayName = (i.symbol || i.note) ? (i.symbol || i.note) : (i.platform ? UNSPECIFIED_LABEL : null);
          if (!displayName) return false;
          if (displayName.trim().toLowerCase() !== assetFocus.trim().toLowerCase()) return false;
          // Respeta el mismo buscador de mes/año de arriba de la página —
          // si hay un mes concreto elegido, solo se ven las compras de ESE
          // mes, no todo el histórico (que con el tiempo sería una lista
          // interminable). Con "Todos los años"/"Todos los meses" se ve
          // todo, igual que antes.
          if (fYear !== "Todos" && i.year !== parseInt(fYear)) return false;
          if (fMonth !== "Todos" && i.month !== fMonth) return false;
          return true;
        })
        .sort((a, b) => {
          const da = new Date(a.year, MONTHS.indexOf(a.month), 1);
          const db2 = new Date(b.year, MONTHS.indexOf(b.month), 1);
          return db2 - da;
        })
    : [];
  const assetFocusTotal = assetFocusItems.reduce((s, i) => s + (i.amount || 0), 0);

  // Precio en vivo para el activo destacado (si ya se pulsó "Actualizar
  // precios" y es una cripto reconocida) — para mostrar la ganancia/pérdida
  // total y por cada compra individual.
  const assetFocusGroup = assetGroups.find(g => g.name.trim().toLowerCase() === (assetFocus || "").trim().toLowerCase());
  const assetFocusEffectiveId = assetFocusGroup
    ? (assetFocusGroup.coingeckoId || (assetFocusGroup.symbol ? resolvedBySymbol[assetFocusGroup.symbol] : null))
    : null;
  const assetFocusLivePrice = assetFocusEffectiveId ? livePrices[assetFocusEffectiveId] : null;
  const assetFocusHasQty = assetFocusGroup && !assetFocusGroup.qtyBroken && assetFocusGroup.qty > 0;
  const assetFocusCurrentValue = (assetFocusLivePrice && assetFocusHasQty) ? assetFocusGroup.qty * assetFocusLivePrice : null;
  const assetFocusDiff = assetFocusCurrentValue !== null ? assetFocusCurrentValue - assetFocusTotal : null;
  const assetFocusDiffPct = (assetFocusDiff !== null && assetFocusTotal > 0) ? (assetFocusDiff / assetFocusTotal) * 100 : null;

  function clearAssetFocus() {
    setAssetFocus(null);
    setVisiblePurchases(PURCHASES_PAGE_SIZE);
    navigate("/inversiones", { replace: true });
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><TrendingUp size={22}/> {lang === "en" ? "Investments" : "Inversiones"}</h2>
          <p className="page-sub">
            {lang === "en"
              ? "Track how much you invest each month — resets automatically every month"
              : "Registra cuánto inviertes cada mes — se reinicia automáticamente cada mes"}
          </p>
        </div>
      </div>

      {/* Total invertido de siempre, con su beneficio/pérdida agregado + disponible para invertir */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,260px),1fr))", gap:14, marginBottom:20}}>
        <div className="card" style={{background:"var(--gray-900, #1a1a1a)", color:"#fff"}}>
          <div style={{fontSize:12, letterSpacing:"0.06em", textTransform:"uppercase", opacity:0.7, marginBottom:6}}>
            {periodTotalLabel}
          </div>
          <div style={{fontSize:32, fontWeight:700}}>{fmt(periodInvested)}</div>
          {periodHasAnyLive ? (
            <div style={{fontSize:14, fontWeight:600, marginTop:6, display:"flex", alignItems:"center", gap:4,
                         color: periodDiff >= 0 ? "#6ee7a8" : "#ff9b8a"}}>
              {periodDiff >= 0 ? <ArrowUp size={13}/> : <ArrowDown size={13}/>} {periodDiff >= 0 ? "+" : ""}{fmt(periodDiff)} ({periodDiffPct !== null ? `${periodDiffPct >= 0 ? "+" : ""}${periodDiffPct.toFixed(1)}%` : "—"})
              <span style={{opacity:0.6, fontWeight:400}}> {lang === "en" ? "profit" : "beneficio"}</span>
            </div>
          ) : (
            <div style={{fontSize:12, opacity:0.6, marginTop:4}}>
              {lang === "en"
                ? "Add a crypto asset with live price to see profit/loss here."
                : "Añade un activo cripto con precio en vivo para ver el beneficio aquí."}
            </div>
          )}
        </div>
        <div className="card" style={{border:"1.5px solid var(--green,#1B7F3A)"}}>
          <div style={{fontSize:12, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--green,#1B7F3A)", marginBottom:6}}>
            {availableCardLabel}
          </div>
          <div style={{fontSize:32, fontWeight:700, color:"var(--green,#1B7F3A)"}}>{fmt(availableCardValue)}</div>
          <div style={{fontSize:12, color:"var(--gray-400)", marginTop:4}}>
            {availableCardSub}
          </div>
        </div>
      </div>

      {/* Buscador: filtrar por año y/o mes */}
      <div className="filters" style={{marginBottom:20}}>
        <select className="filter-select" value={fYear} onChange={e=>setFYear(e.target.value)}>
          <option value="Todos">{lang === "en" ? "All years" : "Todos los años"}</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="filter-select" value={fMonth} onChange={e=>setFMonth(e.target.value)}>
          <option value="Todos">{lang === "en" ? "All months" : "Todos los meses"}</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={m}>{lang === "en" ? MONTHS_EN[i] : m}</option>
          ))}
        </select>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,420px),1fr))", gap:20, alignItems:"start"}}>

        {/* Formulario */}
        <div className="card">
          {!showForm ? (
            <button className="btn btn-primary" style={{width:"100%", justifyContent:"center", padding:"12px", display:"flex", alignItems:"center", gap:6}}
                    onClick={()=>setShowForm(true)}>
              <Plus size={16}/> {lang === "en" ? "Add entry" : "Añadir entrada"}
            </button>
          ) : (
          <>
          <h3 style={{fontSize:15, fontWeight:600, marginBottom:16, display:"flex", alignItems:"center", gap:6}}>
            <Wallet size={16}/> {lang === "en" ? "Investments movement" : "Movimiento en Inversiones"}
          </h3>

          <div style={{display:"flex", gap:8, marginBottom:16}}>
            <button type="button"
                    className={mode === "add" ? "btn btn-primary" : "btn btn-outline"}
                    style={{flex:1, justifyContent:"center", display:"flex", alignItems:"center", gap:6}}
                    onClick={() => setMode("add")}>
              <Plus size={15}/> {lang === "en" ? "Contribute" : "Aportar"}
            </button>
            <button type="button"
                    className={mode === "withdraw" ? "btn btn-primary" : "btn btn-outline"}
                    style={{flex:1, justifyContent:"center", display:"flex", alignItems:"center", gap:6}}
                    onClick={() => setMode("withdraw")}>
              <Minus size={15}/> {lang === "en" ? "Withdraw" : "Retirar"}
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success" style={{display:"flex",alignItems:"center",gap:6}}><CircleCheck size={15}/> {success}</div>}
          <form onSubmit={handleAdd}>
            <div className="form-group">
              <label className="form-label">{lang === "en" ? "Date" : "Fecha"}</label>
              <input className="form-input" type="date" value={entryDate}
                     onChange={e => setEntryDate(e.target.value)} required />
              {selectedCoin && (
                <p className="form-hint">
                  {lang === "en"
                    ? "The crypto price above will update automatically for this date."
                    : "El precio de la cripto de arriba se actualizará automáticamente para esta fecha."}
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">{lang === "en" ? "Amount" : "Importe"}</label>
              <input className="form-input" type="number" step="0.01" min="0.01"
                     value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" required />
              {mode === "add" && amount && parseFloat(String(amount).replace(",",".")) > 0 && (
                parseFloat(String(amount).replace(",",".")) <= availableToInvest ? (
                  <p className="form-hint" style={{color:"var(--green,#1B7F3A)",display:"flex",alignItems:"center",gap:5}}>
                    <CircleCheck size={13}/> {lang === "en"
                      ? "Covered by your auto-set-aside fund — won't affect your available balance."
                      : "Cubierto por tu fondo automático — no afectará a tu Saldo Disponible."}
                  </p>
                ) : (
                  <p className="form-hint" style={{color:"var(--red,#C0392B)",display:"flex",alignItems:"center",gap:5}}>
                    <AlertTriangle size={13}/> {lang === "en"
                      ? "Exceeds your auto-set-aside fund — will be subtracted from your available balance and logged in History."
                      : "Supera tu fondo automático — se restará de tu Saldo Disponible y quedará reflejado en el Historial."}
                  </p>
                )
              )}
              {mode === "withdraw" && (
                <p className="form-hint">
                  {lang === "en"
                    ? `This money comes back to your available balance (logged as income). Currently invested: ${fmt(totalInvested)}.`
                    : `Este dinero vuelve a tu saldo disponible (se registra como ingreso). Actualmente invertido: ${fmt(totalInvested)}.`}
                </p>
              )}
            </div>
            <div className="form-group">
              <div style={{display:"flex", gap:10}}>
                <div style={{flex:1}}>
                  <label className="form-label">
                    {lang === "en" ? "Ticker / Symbol" : "Símbolo / Ticker"} <span style={{color:"var(--gray-400)", fontWeight:400}}>({lang === "en" ? "optional" : "opcional"})</span>
                  </label>
                  <input className="form-input" value={symbol} onChange={e => setSymbol(e.target.value)}
                         placeholder="SPCX, SOL, NVDA..." />
                </div>
                <div style={{flex:1}}>
                  <label className="form-label">
                    {lang === "en" ? "Quantity" : "Cantidad"} <span style={{color:"var(--gray-400)", fontWeight:400}}>({lang === "en" ? "optional" : "opcional"})</span>
                  </label>
                  <input className="form-input" type="number" step="any" value={quantity} onChange={e => setQuantity(e.target.value)}
                         placeholder="0.008" />
                </div>
              </div>
              <p className="form-hint">
                {lang === "en"
                  ? "Using the real ticker will let this asset sync with live market prices in the future."
                  : "Usar el ticker real permitirá sincronizar este activo con precios de mercado en el futuro."}
              </p>

              {/* Buscador de cripto con precio en vivo (CoinGecko) */}
              <div style={{marginTop:10}}>
                <input className="form-input" value={cryptoQuery} onChange={e => handleCryptoQueryChange(e.target.value)}
                       placeholder={lang === "en" ? "Search crypto (e.g. Solana, Bitcoin...)" : "Buscar cripto (ej: Solana, Bitcoin...)"} />
                <p style={{fontSize:11, color:"var(--gray-400)", marginTop:4}}>
                  {lang === "en" ? "Prices provided by " : "Precios proporcionados por "}
                  <a href="https://www.coingecko.com" target="_blank" rel="noopener noreferrer" style={{color:"inherit"}}>CoinGecko</a>
                </p>
                {cryptoSearching && (
                  <p className="form-hint">{lang === "en" ? "Searching..." : "Buscando..."}</p>
                )}
                {cryptoResults.length > 0 && (
                  <div style={{border:"1px solid var(--border)", borderRadius:8, marginTop:6, maxHeight:200, overflowY:"auto"}}>
                    {cryptoResults.map(coin => (
                      <div key={coin.id} onClick={() => selectCoin(coin)}
                           style={{padding:"9px 12px", cursor:"pointer", fontSize:13, borderBottom:"1px solid var(--border-soft)"}}
                           onMouseEnter={ev => ev.currentTarget.style.background = "var(--gray-50)"}
                           onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                        {coin.name} <span style={{color:"var(--gray-400)"}}>({coin.symbol?.toUpperCase()})</span>
                      </div>
                    ))}
                  </div>
                )}
                {selectedCoin && coinPriceEur !== null && (() => {
                  const today = new Date().toISOString().slice(0, 10);
                  const isHistorical = entryDate && entryDate !== today;
                  return (
                    <div className="form-hint" style={{color:"var(--green,#1B7F3A)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8}}>
                      <span style={{display:"flex",alignItems:"center",gap:5}}>
                        <CircleCheck size={13}/> {selectedCoin.name} ({selectedCoin.symbol.toUpperCase()}) — 1 {selectedCoin.symbol.toUpperCase()} = {fmt(coinPriceEur)}
                        {isHistorical
                          ? (lang === "en" ? ` (price on ${entryDate})` : ` (precio del ${entryDate})`)
                          : (lang === "en" ? " (today)" : " (hoy)")}
                      </span>
                      <button type="button" onClick={clearSelectedCoin}
                              style={{background:"none", border:"none", color:"var(--gray-400)", cursor:"pointer", flexShrink:0, display:"flex"}}>
                        <X size={13}/>
                      </button>
                    </div>
                  );
                })()}
                {cryptoError && (
                  <p className="form-hint" style={{color:"var(--red,#C0392B)"}}>{cryptoError}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                {lang === "en" ? "Note" : "Nota"} <span style={{color:"var(--gray-400)", fontWeight:400}}>({lang === "en" ? "optional" : "opcional"})</span>
              </label>
              <input className="form-input" value={note} onChange={e => setNote(e.target.value)}
                     placeholder={lang === "en" ? "E.g. Index fund, crypto..." : "Ej: Fondo indexado, cripto..."} />
            </div>
            <div className="form-group">
              <label className="form-label">
                {lang === "en" ? "Investment platform" : "Plataforma de inversión"} <span style={{color:"var(--gray-400)", fontWeight:400}}>({lang === "en" ? "optional" : "opcional"})</span>
              </label>
              <input className="form-input" value={platform} onChange={e => setPlatform(e.target.value)}
                     placeholder={lang === "en" ? "E.g. Binance, Indexa Capital, eToro..." : "Ej: Binance, Indexa Capital, eToro..."} />
            </div>
            <button className="btn btn-primary" style={{width:"100%", justifyContent:"center", padding:"12px", display:"flex", alignItems:"center", gap:6}} type="submit" disabled={saving}>
              {saving ? "..." : mode === "withdraw"
                ? <><Minus size={15}/> {lang === "en" ? "Withdraw" : "Retirar"}</>
                : <><Plus size={15}/> {lang === "en" ? "Contribute" : "Aportar"}</>}
            </button>
          </form>
          </>
          )}

          {currentMonthEntries.length > 0 && (
            <div style={{marginTop:20}}>
              <p style={{fontSize:12, fontWeight:600, color:"var(--gray-600)", marginBottom:8}}>
                {hasSpecificMonthFilter
                  ? (lang === "en" ? `Contributions in ${fMonth} ${fYear}` : `Aportaciones de ${fMonth} ${fYear}`)
                  : (lang === "en" ? "This month's contributions" : "Aportaciones de este mes")}
              </p>
              <div style={{display:"flex", flexDirection:"column", gap:6}}>
                {currentMonthEntries.map(i => (
                  editingId === i.id ? (
                    <div key={i.id} style={{padding:"10px", background:"var(--gray-50)", borderRadius:8, display:"flex", flexDirection:"column", gap:8}}>
                      <input className="form-input" type="number" step="0.01" min="0.01"
                             value={editAmount} onChange={e => setEditAmount(e.target.value)} placeholder="0.00" />
                      <div style={{display:"flex", gap:8}}>
                        <input className="form-input" value={editSymbol} onChange={e => { setEditSymbol(e.target.value); setEditCoingeckoId(null); }}
                               placeholder={lang === "en" ? "Ticker (e.g. SPCX)" : "Ticker (ej: SPCX)"} />
                        <input className="form-input" type="number" step="any" value={editQuantity} onChange={e => setEditQuantity(e.target.value)}
                               placeholder={lang === "en" ? "Quantity" : "Cantidad"} />
                      </div>
                      <div>
                        <input className="form-input" value={editCryptoQuery} onChange={e => handleEditCryptoQueryChange(e.target.value)}
                               placeholder={lang === "en" ? "Search crypto to change it..." : "Buscar cripto para cambiarla..."} />
                        {editCryptoSearching && <p className="form-hint">{lang === "en" ? "Searching..." : "Buscando..."}</p>}
                        {editCryptoResults.length > 0 && (
                          <div style={{border:"1px solid var(--border)", borderRadius:8, marginTop:6, maxHeight:180, overflowY:"auto"}}>
                            {editCryptoResults.map(coin => (
                              <div key={coin.id} onClick={() => selectEditCoin(coin)}
                                   style={{padding:"8px 12px", cursor:"pointer", fontSize:13, borderBottom:"1px solid var(--border-soft)"}}
                                   onMouseEnter={ev => ev.currentTarget.style.background = "var(--gray-50)"}
                                   onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                                {coin.name} <span style={{color:"var(--gray-400)"}}>({coin.symbol?.toUpperCase()})</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {editCoingeckoId && (
                          <p className="form-hint" style={{color:"var(--green,#1B7F3A)"}}>
                            <CircleCheck size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang === "en" ? "Linked for live price updates" : "Vinculada para poder actualizar el precio en vivo"}
                          </p>
                        )}
                      </div>
                      <input className="form-input" value={editNote} onChange={e => setEditNote(e.target.value)}
                             placeholder={lang === "en" ? "Note" : "Nota"} />
                      <input className="form-input" value={editPlatform} onChange={e => setEditPlatform(e.target.value)}
                             placeholder={lang === "en" ? "Platform" : "Plataforma"} />
                      <div style={{display:"flex", gap:8}}>
                        <button className="btn btn-primary btn-sm" style={{flex:1, justifyContent:"center"}}
                                disabled={editSaving} onClick={() => saveEditInv(i)}>
                          {editSaving ? "..." : (lang === "en" ? "Save" : "Guardar")}
                        </button>
                        <button className="btn btn-outline btn-sm" style={{flex:1, justifyContent:"center"}}
                                onClick={() => setEditingId(null)}>
                          {lang === "en" ? "Cancel" : "Cancelar"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div key={i.id} style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                                            padding:"8px 10px", background:"var(--gray-50)", borderRadius:8, fontSize:13}}>
                      <div>
                        <strong style={{color: i.isWithdrawal ? "var(--red,#C0392B)" : "inherit"}}>
                          {i.isWithdrawal ? "− " : ""}{fmt(Math.abs(i.amount))}
                        </strong>
                        {assetLabel(i) && <span style={{color:"var(--gray-500)"}}> · {assetLabel(i)}</span>}
                        {i.note && <span style={{color:"var(--gray-500)"}}> · {i.note}</span>}
                        {i.platform && <span style={{color:"var(--gray-500)"}}> · {i.platform}</span>}
                        {!i.migrated && (
                          <div style={{fontSize:11, color: i.isWithdrawal ? "var(--red,#C0392B)" : (i.coveredByPool ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)")}}>
                            {i.isWithdrawal
                              ? (lang === "en" ? "Withdrawal (added back to balance)" : "Retiro (añadido de vuelta al balance)")
                              : (i.coveredByPool
                                  ? (lang === "en" ? "Covered by fund" : "Cubierta por el fondo")
                                  : (lang === "en" ? "Subtracted from balance" : "Restada del balance"))}
                          </div>
                        )}
                      </div>
                      {!i.migrated && (
                        <div style={{display:"flex", gap:6, flexShrink:0}}>
                          <button className="btn btn-outline btn-sm" onClick={() => startEditInv(i)} style={{display:"flex",alignItems:"center"}}>
                            <Pencil size={13}/>
                          </button>
                          <button className="btn btn-danger btn-sm" style={{display:"flex",alignItems:"center"}}
                                  onClick={() => { if (window.confirm(lang === "en" ? "Delete?" : "¿Eliminar?")) deleteInvestment(i); }}>
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Historial + comparativa */}
        <div>
          {/* Gráfico comparativo */}
          <div className="card" style={{marginBottom:20}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginBottom:14}}>
              <h3 style={{fontSize:15, fontWeight:600}}>
                <BarChart3 size={16} style={{display:"inline",verticalAlign:"-3px",marginRight:6}}/> {lang === "en" ? "Month-to-month comparison" : "Comparativa mes a mes"}
              </h3>
              {/* Los botones de periodo solo aportan algo si hay más meses
                  de los que ya caben en el rango más corto (3M) */}
              {fYear === "Todos" && fMonth === "Todos" && filteredHistory.length > 3 && (
                <div style={{display:"flex", gap:6}}>
                  {[[3, "3M"], [12, lang==="en"?"1Y":"1A"], [Infinity, lang==="en"?"5Y":"5A"]].map(([val, label]) => (
                    <button key={label} type="button" onClick={() => setChartRange(val)}
                            style={{padding:"4px 11px", borderRadius:14, fontSize:11, fontWeight:600, cursor:"pointer",
                                    border:`1.5px solid ${chartRange===val?"var(--gold-dark,#9C7A16)":"var(--border)"}`,
                                    background: chartRange===val ? "var(--gold-dark,#9C7A16)" : "white",
                                    color: chartRange===val ? "white" : "var(--gray-600)"}}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {chartData.length < 2 ? (
              <p style={{fontSize:13, color:"var(--gray-400)"}}>
                {chartData.length === 0
                  ? (lang === "en" ? "Add your first contribution to see the chart." : "Añade tu primera aportación para ver el gráfico.")
                  : (lang === "en"
                      ? "Select more than one month (or remove the filter) to compare."
                      : "Selecciona más de un mes (o quita el filtro) para poder comparar.")}
              </p>
            ) : (() => {
              // Escala ajustada al rango real de los valores (como hace
              // CoinGecko) — variaciones pequeñas entre meses parecidos se
              // ven, en vez de aplastarse arriba con hueco vacío debajo.
              const rawMax = Math.max(...chartData.map(m => m.total), 0);
              const rawMin = Math.min(...chartData.map(m => m.total), rawMax);
              const pad = Math.max((rawMax - rawMin) * 0.15, rawMax * 0.05, 1);
              const scaleMax = rawMax + pad;
              const scaleMin = Math.max(0, rawMin - pad);
              const range = scaleMax - scaleMin || 1;
              // Margen fijo arriba (para la etiqueta del importe, que se
              // dibuja por encima de cada punto) y abajo (para la
              // etiqueta del mes) — sin esto, los puntos más altos
              // recortaban su etiqueta contra el borde superior del lienzo.
              const topPad = 20, bottomPad = 20;
              const points = chartData.map((m, i) => ({
                x: i * slot + slot / 2,
                y: topPad + chartH - ((m.total - scaleMin) / range) * chartH,
                m,
              }));
              // Curva suave (Catmull-Rom a Bézier) en vez de líneas rectas.
              function smoothPath(pts) {
                let d = `M ${pts[0].x},${pts[0].y}`;
                for (let i = 0; i < pts.length - 1; i++) {
                  const p0 = pts[i === 0 ? 0 : i - 1], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
                  const cp1x = p1.x + (p2.x - p0.x) / 6, cp1y = p1.y + (p2.y - p0.y) / 6;
                  const cp2x = p2.x - (p3.x - p1.x) / 6, cp2y = p2.y - (p3.y - p1.y) / 6;
                  d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
                }
                return d;
              }
              const linePath = smoothPath(points);
              const baseline = topPad + chartH;
              const areaPath = `${linePath} L ${points[points.length - 1].x},${baseline} L ${points[0].x},${baseline} Z`;
              const trendUp = points[points.length - 1].m.total >= points[0].m.total;
              const trendColor = trendUp ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)";
              return (
                <svg viewBox={`0 0 ${chartViewW} ${topPad + chartH + bottomPad}`} style={{width:"100%", height:"auto", display:"block", maxHeight:200}}>
                  <defs>
                    <linearGradient id="invLineArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={trendColor} stopOpacity="0.22"/>
                      <stop offset="100%" stopColor={trendColor} stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  <path d={areaPath} fill="url(#invLineArea)" />
                  <path d={linePath} fill="none" stroke={trendColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  {points.map((p, i) => (
                    <g key={i}>
                      {(i === 0 || i === points.length - 1 || points.length <= 6) && (
                        <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="10" fill="var(--gray-600)" fontWeight="600">
                          {fmt(p.m.total)}
                        </text>
                      )}
                      <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke={trendColor} strokeWidth="2.5" />
                      <text x={p.x} y={baseline + 16} textAnchor="middle" fontSize="10" fill="var(--gray-500)">
                        {p.m.month.slice(0,3)} {String(p.m.year).slice(2)}
                      </text>
                    </g>
                  ))}
                </svg>
              );
            })()}
          </div>

          {/* Gráfico donut: qué mes se invirtió más */}
          <div className="card" style={{marginBottom:20}}>
            <h3 style={{fontSize:15, fontWeight:600, marginBottom:16}}>
              <PieChart size={16} style={{display:"inline",verticalAlign:"-3px",marginRight:6}}/> {lang === "en" ? "Which month invested more" : "¿Qué mes se invirtió más?"}
            </h3>
            {chartData.length < 2 || donutTotal <= 0 ? (
              <p style={{fontSize:13, color:"var(--gray-400)"}}>
                {lang === "en"
                  ? "Select more than one month (or remove the filter) to compare."
                  : "Selecciona más de un mes (o quita el filtro) para poder comparar."}
              </p>
            ) : (
              <>
                {topMonth && (
                  <p style={{fontSize:13, color:"var(--gray-600)", marginBottom:14}}>
                    <TrendingUp size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang === "en" ? "Top month:" : "Mes con más inversión:"} <strong>{topMonth.month} {topMonth.year}</strong> ({fmt(topMonth.total)})
                  </p>
                )}
                <div style={{display:"flex", alignItems:"center", gap:22, flexWrap:"wrap"}}>
                  <svg viewBox="0 0 140 140" style={{width:130, height:130, flexShrink:0}}>
                    <g transform="rotate(-90 70 70)">
                      {donutSegments.map((seg, i) => (
                        <circle key={i} cx="70" cy="70" r="50" fill="none" stroke={seg.color} strokeWidth="18"
                                strokeDasharray={`${seg.len} ${donutCircumference - seg.len}`}
                                strokeDashoffset={-seg.offset} />
                      ))}
                    </g>
                  </svg>
                  <div style={{display:"flex", flexDirection:"column", gap:8, flex:1, minWidth:160}}>
                    {donutSegments.map((seg, i) => (
                      <div key={i} style={{display:"flex", alignItems:"center", gap:8, fontSize:13}}>
                        <span style={{width:9, height:9, borderRadius:"50%", background:seg.color, display:"inline-block", flexShrink:0}} />
                        <span style={{color:"var(--gray-600)"}}>{seg.month.slice(0,3)} {String(seg.year).slice(2)}</span>
                        <b style={{marginLeft:"auto"}}>{fmt(seg.total)} <span style={{color:"var(--gray-400)", fontWeight:400}}>({seg.segPct.toFixed(0)}%)</span></b>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Totales por activo (agrupado por la Nota, ej. "cripto", "Nvidia") */}
          {assetGroups.length > 0 && (
            <div className="card" style={{marginBottom:20}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4, flexWrap:"wrap", gap:10}}>
                <h3 style={{fontSize:15, fontWeight:600}}>
                  <Package size={16} style={{display:"inline",verticalAlign:"-3px",marginRight:6}}/> {lang === "en" ? "Totals by asset" : "Totales por activo"}
                </h3>
                {assetGroups.some(g => g.coingeckoId || g.symbol) && (
                  <button type="button" className="btn btn-outline btn-sm" disabled={pricesLoading} onClick={refreshPrices} style={{display:"flex",alignItems:"center",gap:5}}>
                    <RefreshCw size={13}/> {pricesLoading
                      ? (lang === "en" ? "Updating..." : "Actualizando...")
                      : (lang === "en" ? "Update prices" : "Actualizar precios")}
                  </button>
                )}
              </div>
              <p style={{fontSize:12, color:"var(--gray-400)", marginBottom:6}}>
                {lang === "en"
                  ? `Grouped by symbol/note — showing: ${periodTotalLabel}.`
                  : `Agrupado por símbolo/nota — mostrando: ${periodTotalLabel}.`}
              </p>
              {pricesUpdatedAt && (
                <p style={{fontSize:11, color:"var(--gray-400)", marginBottom:4}}>
                  {lang === "en" ? "Prices updated" : "Precios actualizados"} {pricesUpdatedAt.toLocaleTimeString(lang === "en" ? "en-GB" : "es-ES")}
                  {" · "}
                  <a href="https://www.coingecko.com" target="_blank" rel="noopener noreferrer" style={{color:"inherit"}}>
                    {lang === "en" ? "data by CoinGecko" : "datos de CoinGecko"}
                  </a>
                </p>
              )}
              {pricesError && <p style={{fontSize:12, color:"var(--red,#C0392B)", marginBottom:10}}>{pricesError}</p>}

              {/* Buscador por nombre/símbolo */}
              <div className="form-group" style={{marginBottom:10}}>
                <input className="form-input" value={assetSearch} onChange={e => setAssetSearch(e.target.value)}
                       placeholder={lang === "en" ? "Search by asset (e.g. SOL, Nvidia...)" : "Buscar por activo (ej: SOL, Nvidia...)"} />
              </div>

              {/* Total invertido + beneficio, solo de los resultados de la búsqueda */}
              {assetSearch.trim() && displayedAssetGroups.length > 0 && (
                <div style={{display:"flex", gap:10, marginBottom:12, flexWrap:"wrap"}}>
                  <div style={{background:"var(--gray-100)", borderRadius:20, padding:"8px 14px", fontSize:13, fontWeight:600}}>
                    {lang === "en" ? "Invested" : "Invertido"}: {fmt(searchInvested)}
                  </div>
                  {searchHasAnyLive && (
                    <div style={{background: searchDiff >= 0 ? "var(--green-l,rgba(27,127,58,.1))" : "var(--red-l,rgba(192,57,43,.1))",
                                 borderRadius:20, padding:"8px 14px", fontSize:13, fontWeight:600,
                                 color: searchDiff >= 0 ? "var(--green)" : "var(--red)", display:"inline-flex", alignItems:"center", gap:4}}>
                      {searchDiff >= 0 ? <ArrowUp size={13}/> : <ArrowDown size={13}/>} {searchDiff >= 0 ? "+" : ""}{fmt(searchDiff)} ({searchDiffPct !== null ? `${searchDiffPct >= 0 ? "+" : ""}${searchDiffPct.toFixed(1)}%` : "—"})
                    </div>
                  )}
                </div>
              )}
              {assetSearch.trim() && displayedAssetGroups.length === 0 && (
                <p style={{fontSize:13, color:"var(--gray-400)", marginBottom:10}}>
                  {lang === "en" ? "No assets match that search." : "Ningún activo coincide con esa búsqueda."}
                </p>
              )}

              <div style={{display:"flex", flexDirection:"column", gap:8}}>
                {displayedAssetGroups.map((g, i) => {
                  const effectiveId = g.coingeckoId || (g.symbol ? resolvedBySymbol[g.symbol] : null);
                  const livePrice = effectiveId ? livePrices[effectiveId] : null;
                  const canShowGainLoss = livePrice && !g.qtyBroken && g.qty > 0;
                  const currentValue = canShowGainLoss ? g.qty * livePrice : null;
                  const diff = canShowGainLoss ? currentValue - g.total : null;
                  const diffPct = canShowGainLoss && g.total > 0 ? (diff / g.total) * 100 : null;
                  const isUp = diff !== null && diff >= 0;
                  const isExpanded = assetFocus === g.name;
                  return (
                    <div key={i} ref={isExpanded ? expandedRowRef : null}>
                      <div style={{padding:"10px 12px", background:isExpanded?"white":"var(--gray-50)", borderRadius:8, fontSize:13, cursor:"pointer",
                                   border:`1.5px solid ${isExpanded?"var(--gold-dark,#9C7A16)":"transparent"}`}}
                           onClick={() => {
                             const next = isExpanded ? null : g.name;
                             setAssetFocus(next);
                             setVisiblePurchases(PURCHASES_PAGE_SIZE);
                             navigate(next ? `/inversiones?asset=${encodeURIComponent(g.name)}` : "/inversiones", { replace:true });
                           }}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8}}>
                          <div>
                            <strong>{g.name}</strong>
                            {g.assetName && <span style={{fontSize:12, color:"var(--gray-400)", marginLeft:6}}>{g.assetName}</span>}
                            <span style={{fontSize:11, color:"var(--gray-400)", marginLeft:6, display:"inline-flex", verticalAlign:"middle"}}>{isExpanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}</span>
                            {g.unit && !g.qtyBroken && (
                              <div style={{fontSize:12, color:"var(--gray-500)"}}>
                                {g.qty} {g.unit}
                              </div>
                            )}
                            {g.qtyBroken && (
                              <div style={{fontSize:11, color:"var(--gray-400)"}}>
                                {lang === "en" ? "Mixed units — can't sum quantity" : "Unidades distintas — no se puede sumar la cantidad"}
                              </div>
                            )}
                          </div>

                          {/* Sin precio en vivo (SpaceX, Nvidia, S&P500...): solo lo invertido, igual que siempre */}
                          {!canShowGainLoss && (
                            <div style={{textAlign:"right", flexShrink:0}}>
                              <b>{fmt(g.total)}</b>
                              <div style={{fontSize:10, color:"var(--gray-400)"}}>
                                {lang === "en" ? "invested" : "invertido"}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Con precio en vivo (cripto): "Invertido → Ahora" en su propia fila, con flecha */}
                        {canShowGainLoss && (
                          <div style={{display:"flex", alignItems:"center", justifyContent:"flex-end", gap:10, marginTop:8, flexWrap:"wrap"}}>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:13, fontWeight:600, color:"var(--gray-500)"}}>{fmt(g.total)}</div>
                              <div style={{fontSize:10, color:"var(--gray-400)"}}>{lang === "en" ? "invested" : "invertido"}</div>
                            </div>
                            <span style={{color: isUp ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)", display:"inline-flex"}}>
                              {isUp ? <ArrowUp size={15}/> : <ArrowDown size={15}/>}
                            </span>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:14, fontWeight:700, color: isUp ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)"}}>
                                {fmt(currentValue)}
                              </div>
                              <div style={{fontSize:10, color:"var(--gray-400)"}}>{lang === "en" ? "now" : "ahora"}</div>
                            </div>
                            <div style={{fontSize:12, fontWeight:600, marginLeft:4,
                                         color: isUp ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)"}}>
                              ({diffPct !== null ? `${diffPct >= 0 ? "+" : ""}${diffPct.toFixed(1)}%` : "—"})
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Acordeón: detalle de compras, colapsado bajo la fila */}
                      {isExpanded && (
                        <div style={{padding:"12px 14px", background:"white", border:"1.5px solid var(--gold-dark,#9C7A16)",
                                     borderTop:"none", borderBottomLeftRadius:8, borderBottomRightRadius:8}}
                             onClick={ev => ev.stopPropagation()}>
                          {(assetFocusGroup?.coingeckoId || assetFocusGroup?.symbol) && (
                            <div style={{display:"flex", justifyContent:"flex-end", marginBottom:10}}>
                              <button type="button" className="btn btn-outline btn-sm" disabled={pricesLoading} onClick={refreshPrices} style={{display:"flex",alignItems:"center",gap:5}}>
                                <RefreshCw size={13}/> {pricesLoading
                                  ? (lang === "en" ? "Updating..." : "Actualizando...")
                                  : (lang === "en" ? "Update" : "Actualizar")}
                              </button>
                            </div>
                          )}
                          {assetFocusCurrentValue === null && (assetFocusGroup?.coingeckoId || assetFocusGroup?.symbol) && (
                            <p className="form-hint" style={{marginBottom:10}}>
                              {lang === "en"
                                ? "Press \"Update\" above to see the current value and gain/loss."
                                : "Pulsa \"Actualizar\" arriba para ver el valor actual y la ganancia/pérdida."}
                            </p>
                          )}

                          {assetFocusItems.length === 0 ? (
                            <p style={{fontSize:13, color:"var(--gray-400)"}}>
                              {lang==="en" ? "No entries found for this asset." : "No se encontraron movimientos de este activo."}
                            </p>
                          ) : (
                            <>
                              <p style={{fontSize:11, fontWeight:600, color:"var(--gray-400)", textTransform:"uppercase", letterSpacing:".04em", marginBottom:8}}>
                                {lang==="en" ? "Purchase history" : "Historial de compras"} · {periodTotalLabel} ({assetFocusItems.length})
                              </p>
                              <div style={{display:"flex", flexDirection:"column", gap:8}}>
                                {assetFocusItems.slice(0, visiblePurchases).map(it => {
                                  // Ganancia/pérdida de ESTA compra en concreto: lo que vale
                                  // hoy su cantidad, frente a lo que se pagó entonces.
                                  const itemQty = it.quantity !== null && it.quantity !== undefined ? Math.abs(it.quantity) : null;
                                  const itemCurrentValue = (assetFocusLivePrice && itemQty) ? itemQty * assetFocusLivePrice : null;
                                  const itemDiff = itemCurrentValue !== null ? itemCurrentValue - Math.abs(it.amount) : null;
                                  const itemDiffPct = (itemDiff !== null && it.amount) ? (itemDiff / Math.abs(it.amount)) * 100 : null;

                                  if (editingId === it.id) {
                                    return (
                                      <div key={it.id} style={{padding:"10px", background:"var(--gray-50)", borderRadius:8, display:"flex", flexDirection:"column", gap:8}}>
                                        <input className="form-input" type="number" step="0.01" min="0.01"
                                               value={editAmount} onChange={e => setEditAmount(e.target.value)} placeholder="0.00" />
                                        <div style={{display:"flex", gap:8}}>
                                          <input className="form-input" value={editSymbol} onChange={e => { setEditSymbol(e.target.value); setEditCoingeckoId(null); }}
                                                 placeholder={lang === "en" ? "Ticker (e.g. SPCX)" : "Ticker (ej: SPCX)"} />
                                          <input className="form-input" type="number" step="any" value={editQuantity} onChange={e => setEditQuantity(e.target.value)}
                                                 placeholder={lang === "en" ? "Quantity" : "Cantidad"} />
                                        </div>
                                        <div>
                                          <input className="form-input" value={editCryptoQuery} onChange={e => handleEditCryptoQueryChange(e.target.value)}
                                                 placeholder={lang === "en" ? "Search crypto to change it..." : "Buscar cripto para cambiarla..."} />
                                          {editCryptoSearching && <p className="form-hint">{lang === "en" ? "Searching..." : "Buscando..."}</p>}
                                          {editCryptoResults.length > 0 && (
                                            <div style={{border:"1px solid var(--border)", borderRadius:8, marginTop:6, maxHeight:180, overflowY:"auto"}}>
                                              {editCryptoResults.map(coin => (
                                                <div key={coin.id} onClick={() => selectEditCoin(coin)}
                                                     style={{padding:"8px 12px", cursor:"pointer", fontSize:13, borderBottom:"1px solid var(--border-soft)"}}
                                                     onMouseEnter={ev => ev.currentTarget.style.background = "var(--gray-50)"}
                                                     onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                                                  {coin.name} <span style={{color:"var(--gray-400)"}}>({coin.symbol?.toUpperCase()})</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {editCoingeckoId && (
                                            <p className="form-hint" style={{color:"var(--green,#1B7F3A)"}}>
                                              <CircleCheck size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang === "en" ? "Linked for live price updates" : "Vinculada para poder actualizar el precio en vivo"}
                                            </p>
                                          )}
                                        </div>
                                        <input className="form-input" value={editNote} onChange={e => setEditNote(e.target.value)}
                                               placeholder={lang === "en" ? "Note" : "Nota"} />
                                        <input className="form-input" value={editPlatform} onChange={e => setEditPlatform(e.target.value)}
                                               placeholder={lang === "en" ? "Platform" : "Plataforma"} />
                                        <div style={{display:"flex", gap:8}}>
                                          <button className="btn btn-primary btn-sm" style={{flex:1, justifyContent:"center"}}
                                                  disabled={editSaving} onClick={() => saveEditInv(it)}>
                                            {editSaving ? "..." : (lang === "en" ? "Save" : "Guardar")}
                                          </button>
                                          <button className="btn btn-outline btn-sm" style={{flex:1, justifyContent:"center"}}
                                                  onClick={() => setEditingId(null)}>
                                            {lang === "en" ? "Cancel" : "Cancelar"}
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div key={it.id} style={{padding:"8px 10px", background:"var(--gray-50)", borderRadius:8, fontSize:13}}>
                                      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
                                        <div>
                                          <strong style={{color: it.isWithdrawal ? "var(--red,#C0392B)" : "inherit"}}>
                                            {it.isWithdrawal ? "− " : ""}{fmt(Math.abs(it.amount))}
                                          </strong>
                                          {assetLabel(it) && <span style={{color:"var(--gray-500)"}}> · {assetLabel(it)}</span>}
                                          {it.platform && <span style={{color:"var(--gray-500)"}}> · {it.platform}</span>}
                                          <div style={{fontSize:11, color:"var(--gray-400)"}}>{it.datetime || `${it.month} ${it.year}`}</div>
                                        </div>
                                        <div style={{display:"flex", alignItems:"center", gap:8, flexShrink:0}}>
                                          {itemCurrentValue !== null && !it.isWithdrawal && (
                                            <div style={{textAlign:"right"}}>
                                              <div style={{fontSize:13, fontWeight:700, color: itemDiff >= 0 ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)"}}>
                                                {fmt(itemCurrentValue)}
                                              </div>
                                              <div style={{fontSize:11, fontWeight:600, color: itemDiff >= 0 ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)", display:"flex", alignItems:"center", justifyContent:"flex-end", gap:3}}>
                                                {itemDiff >= 0 ? <ArrowUp size={11}/> : <ArrowDown size={11}/>} {itemDiffPct !== null ? `${itemDiffPct >= 0 ? "+" : ""}${itemDiffPct.toFixed(1)}%` : ""}
                                              </div>
                                            </div>
                                          )}
                                          {!it.migrated && (
                                            <div style={{display:"flex", gap:4}}>
                                              <button className="btn btn-outline btn-sm" onClick={() => startEditInv(it)} style={{display:"flex",alignItems:"center"}}>
                                                <Pencil size={13}/>
                                              </button>
                                              <button className="btn btn-danger btn-sm" style={{display:"flex",alignItems:"center"}}
                                                      onClick={() => { if (window.confirm(lang === "en" ? "Delete?" : "¿Eliminar?")) deleteInvestment(it); }}>
                                                <Trash2 size={13}/>
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {visiblePurchases < assetFocusItems.length && (
                                <button type="button" className="btn btn-outline btn-sm" style={{width:"100%", justifyContent:"center", marginTop:10}}
                                        onClick={() => setVisiblePurchases(v => v + PURCHASES_PAGE_SIZE)}>
                                  {lang === "en"
                                    ? `See more (${assetFocusItems.length - visiblePurchases} left)`
                                    : `Ver más (${assetFocusItems.length - visiblePurchases} restantes)`}
                                </button>
                              )}
                              {visiblePurchases > PURCHASES_PAGE_SIZE && (
                                <button type="button" onClick={() => setVisiblePurchases(PURCHASES_PAGE_SIZE)}
                                        style={{background:"none", border:"none", color:"var(--gray-400)", fontSize:12, marginTop:8, cursor:"pointer", width:"100%"}}>
                                  {lang === "en" ? "Show less" : "Ver menos"}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
